// see https://github.com/orgs/vuejs/discussions/11798
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { type RouteRecordRaw } from 'vue-router';
import { Navigation } from '@/router/navigation';
import { initNavAreaByProvisionType } from '@/router/guards/init-nav-area';
import { initCart } from '@/router/guards/init-cart';
import { ensureProvisionType } from '@/router/guards/ensure-provision-type';
import { ProductCategory } from '@/util/product-category';
import { setNavAreaSwitchEnd, setNavAreaSwitchStart } from './guards/set-nav-area-switch';
import { clearProductListViewDataHistoryStateOnRefresh } from './guards/clear-product-listview-data-history-state-on-refresh';
import { requirePermissionOrLoggedOut } from '@/router/guards/require-permission-or-logged-out.ts';
import { PermissionName } from '@containex/portal-backend-dto';

export const productRoutes: RouteRecordRaw = {
    path: '',
    children: [
        {
            path: 'sales',
            component: async () => import('../views/BaseRouterView.vue'),
            redirect: (to) => ({
                ...to,
                name: Navigation.SalesProductList,
            }),
            children: [
                {
                    path: 'products',
                    beforeEnter: [
                        setNavAreaSwitchStart,
                        ensureProvisionType,
                        initNavAreaByProvisionType,
                        initCart,
                        setNavAreaSwitchEnd,
                    ],
                    children: [
                        {
                            path: '',
                            name: Navigation.SalesProductList,
                            props: () => ({
                                preSelectedFacets: [],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.ALL_PRODUCTS',
                                productCategory: ProductCategory.SALES_ALL,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/sales/views/SalesProductListView.vue'),
                        },
                        {
                            path: 'new',
                            name: Navigation.SalesProductListNew,
                            props: () => ({
                                preSelectedFacets: ['isUsed:false'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.NEW_PRODUCTS',
                                productCategory: ProductCategory.SALES_NEW,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/sales/views/SalesProductListView.vue'),
                        },
                        {
                            path: 'used',
                            name: Navigation.SalesProductListUsed,
                            props: () => ({
                                preSelectedFacets: ['isUsed:true'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.USED_PRODUCTS',
                                productCategory: ProductCategory.SALES_USED,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/sales/views/SalesProductListView.vue'),
                        },
                        {
                            path: 'sale',
                            name: Navigation.SalesProductListSale,
                            props: () => ({
                                preSelectedFacets: ['discountArticles:true'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.SALE',
                                productCategory: ProductCategory.DISCOUNT,
                            }),
                            beforeEnter: [
                                clearProductListViewDataHistoryStateOnRefresh,
                                requirePermissionOrLoggedOut(PermissionName.SALESCATALOG_VIEW),
                            ],
                            component: async () => import('../product/sales/views/SalesProductListView.vue'),
                        },
                        {
                            path: 'details/:id/:variantId?',
                            name: Navigation.SalesProductDetail,
                            props: true,
                            component: async () => import('../product/sales/views/SalesProductDetailView.vue'),
                        },
                        {
                            name: Navigation.SalesNotFound,
                            path: 'not-found',
                            component: async () => import('../views/NotFoundView.vue'),
                            props: () => ({
                                redirectRouteName: Navigation.SalesProductList,
                            }),
                            meta: { indexable: false },
                        },
                        {
                            path: ':pathMatch(.*)*',
                            redirect: (to) => ({
                                ...to,
                                name: Navigation.SalesNotFound,
                            }),
                            meta: { indexable: false },
                        },
                    ],
                },
                {
                    path: ':pathMatch(.*)*',
                    redirect: (to) => ({
                        ...to,
                        name: Navigation.SalesNotFound,
                    }),
                    meta: { indexable: false },
                },
            ],
        },
        {
            path: 'rental',
            component: async () => import('../views/BaseRouterView.vue'),
            redirect: (to) => ({
                ...to,
                name: Navigation.RentalProductList,
            }),
            children: [
                {
                    path: 'products',
                    beforeEnter: [
                        setNavAreaSwitchStart,
                        ensureProvisionType,
                        initNavAreaByProvisionType,
                        initCart,
                        setNavAreaSwitchEnd,
                    ],
                    children: [
                        {
                            path: '',
                            name: Navigation.RentalProductList,
                            props: () => ({
                                preSelectedFacets: [],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.ALL_PRODUCTS',
                                productCategory: ProductCategory.RENTAL_ALL,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/rental/views/RentalProductListView.vue'),
                        },
                        {
                            path: 'sanitary',
                            name: Navigation.RentalProductListSanitary,
                            props: () => ({
                                preSelectedFacets: ['productCategory:SANITARY'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.SANITARY_CONTAINERS',
                                productCategory: ProductCategory.RENTAL_SANITARY,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/rental/views/RentalProductListView.vue'),
                        },
                        {
                            path: 'storage',
                            name: Navigation.RentalProductListStorage,
                            props: () => ({
                                preSelectedFacets: ['productCategory:STORAGE'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.STORAGE_CONTAINERS',
                                productCategory: ProductCategory.RENTAL_STORAGE,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/rental/views/RentalProductListView.vue'),
                        },
                        {
                            path: 'office',
                            name: Navigation.RentalProductListOffice,
                            props: () => ({
                                preSelectedFacets: ['productCategory:OFFICE'],
                                headlineTranslationKey: 'NAVIGATION.MENU_ITEM.OFFICE_CONTAINERS',
                                productCategory: ProductCategory.RENTAL_OFFICE,
                            }),
                            beforeEnter: [clearProductListViewDataHistoryStateOnRefresh],
                            component: async () => import('../product/rental/views/RentalProductListView.vue'),
                        },
                        {
                            path: 'details/:id/:variantId?',
                            name: Navigation.RentalProductDetail,
                            props: true,
                            component: async () => import('../product/rental/views/RentalProductDetailView.vue'),
                        },
                        {
                            name: Navigation.RentalNotFound,
                            path: 'not-found',
                            component: async () => import('../views/NotFoundView.vue'),
                            props: () => ({
                                redirectRouteName: Navigation.RentalProductList,
                            }),
                            meta: { indexable: false },
                        },
                        {
                            path: ':pathMatch(.*)*',
                            redirect: (to) => ({
                                ...to,
                                name: Navigation.RentalNotFound,
                            }),
                            meta: { indexable: false },
                        },
                    ],
                },
                {
                    path: ':pathMatch(.*)*',
                    redirect: (to) => ({
                        ...to,
                        name: Navigation.RentalNotFound,
                    }),
                    meta: { indexable: false },
                },
            ],
        },
    ],
};
